import { Maybe } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import getPercentageValue from './getPercentageValue'

interface ScoreRange {
  minScore: number
  maxScore: number
}

const scoreRanges: ScoreRange[] = [
  { minScore: -100, maxScore: -61 },
  { minScore: -60, maxScore: -31 },
  { minScore: -30, maxScore: -1 },
  { minScore: 0, maxScore: 10 },
  { minScore: 11, maxScore: 30 },
  { minScore: 31, maxScore: 60 },
  { minScore: 61, maxScore: 100 },
] as const

export const getScore = (
  promoters: number | Maybe<number> | undefined,
  detractors: number | Maybe<number> | undefined,
  respondents: number | Maybe<number> | undefined
) => {
  const promotersVal = promoters ?? 0
  const detractorsVal = detractors ?? 0

  if (!respondents) return 0

  // Rounding modified so values would match the distribution chart
  const promotersPercentage = getPercentageValue(promotersVal, respondents)
  const detractorsPercentage = getPercentageValue(detractorsVal, respondents)
  return promotersPercentage - detractorsPercentage
}

export const getScoreRange = (score: number): ScoreRange | undefined =>
  scoreRanges.find((item: ScoreRange) => score >= item.minScore && score <= item.maxScore)

export const getScoreDescription = (currentRange: ScoreRange | undefined) => {
  if (!currentRange) {
    return i18n.t('enps.analytics.score.msg_100to61')
  }

  const from = Math.abs(currentRange.minScore)
  const to = Math.abs(currentRange.maxScore)

  if (from === 0 && to === 10) {
    return i18n.t('enps.analytics.score.msg_0to10')
  }

  if (from === 100 && to === 61) {
    return i18n.t('enps.analytics.score.msg_100to61')
  }

  if (from === 11 && to === 30) {
    return i18n.t('enps.analytics.score.msg_11to30')
  }

  if (from === 30 && to === 1) {
    return i18n.t('enps.analytics.score.msg_30to1')
  }

  if (from === 31 && to === 60) {
    return i18n.t('enps.analytics.score.msg_31to60')
  }

  if (from === 60 && to === 31) {
    return i18n.t('enps.analytics.score.msg_60to31')
  }

  if (from === 61 && to === 100) {
    return i18n.t('enps.analytics.score.msg_61to100')
  }

  throw new Error(`Unknown score range: ${from} - ${to}`)
}
